import { useState, useEffect } from 'react';
import axios from 'axios';
import { authAxios } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPincode = {
  pincode: '',
  area_name: '',
  is_active: true
};

export default function AdminPincodes() {
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPincode, setEditingPincode] = useState(null);
  const [formData, setFormData] = useState(emptyPincode);

  useEffect(() => {
    fetchPincodes();
  }, []);

  const fetchPincodes = async () => {
    try {
      const response = await authAxios.get(`${API}/pincodes/all`);
      setPincodes(response.data);
    } catch (error) {
      toast.error('Failed to fetch pincodes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPincode) {
        await authAxios.put(`${API}/pincodes/${editingPincode.id}`, formData);
        toast.success('Pincode updated');
      } else {
        await authAxios.post(`${API}/pincodes`, formData);
        toast.success('Pincode added');
      }
      setDialogOpen(false);
      setEditingPincode(null);
      setFormData(emptyPincode);
      fetchPincodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pincode');
    }
  };

  const handleEdit = (pincode) => {
    setEditingPincode(pincode);
    setFormData({
      pincode: pincode.pincode,
      area_name: pincode.area_name,
      is_active: pincode.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    
    try {
      await authAxios.delete(`${API}/pincodes/${id}`);
      toast.success('Pincode deleted');
      fetchPincodes();
    } catch (error) {
      toast.error('Failed to delete pincode');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pincodes">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Delivery Pincodes</h1>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingPincode(null);
            setFormData(emptyPincode);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700" data-testid="add-pincode-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Pincode
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingPincode ? 'Edit Pincode' : 'Add Pincode'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Pincode</Label>
                <Input
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  placeholder="e.g. 247776"
                  maxLength={6}
                  required
                />
              </div>

              <div>
                <Label>Area Name</Label>
                <Input
                  value={formData.area_name}
                  onChange={(e) => setFormData({ ...formData, area_name: e.target.value })}
                  placeholder="Enter area name"
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active (Delivery Available)</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  {editingPincode ? 'Update' : 'Add Pincode'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pincodes Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Pincode</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Area Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pincodes.map((pincode) => (
              <tr key={pincode.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="font-mono font-medium">{pincode.pincode}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{pincode.area_name}</td>
                <td className="px-4 py-3">
                  <Badge variant={pincode.is_active ? 'default' : 'secondary'}>
                    {pincode.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(pincode)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(pincode.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {pincodes.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            No pincodes found. Add your first delivery area!
          </div>
        )}
      </div>
    </div>
  );
}
